import { APPLE_CHIPS, GPUS, MACHINE_PRESETS } from './catalog.js';
import type { AccelKind, AppleChip, GpuProfile, MachineProfile, OsFamily, RuntimeId } from './types.js';

export interface DetectedMachine {
  confidence: 'high' | 'medium' | 'low';
  os: OsFamily;
  accel: AccelKind;
  ramGb: number;
  vramGb: number;
  cpuLabel: string;
  appleChip?: AppleChip;
  gpu?: GpuProfile;
  rawUserAgent: string;
  notes: string[];
}

export interface DetectContext {
  userAgent?: string;
  deviceMemoryGb?: number;
  hardwareConcurrency?: number;
  platform?: string;
  gpuRenderer?: string;
}

interface BrowserNavigatorLike {
  userAgent?: string;
  deviceMemory?: number;
  hardwareConcurrency?: number;
  platform?: string;
  userAgentData?: { platform?: string };
}

function gatherContext(): DetectContext {
  const nav = (globalThis as { navigator?: BrowserNavigatorLike }).navigator;
  if (!nav) return {};
  return {
    userAgent: nav.userAgent,
    deviceMemoryGb: nav.deviceMemory,
    hardwareConcurrency: nav.hardwareConcurrency,
    platform: nav.userAgentData?.platform ?? nav.platform,
    gpuRenderer: readGpuRenderer(),
  };
}

interface WebGlHost {
  getContext(id: string): {
    getExtension(name: string): { UNMASKED_RENDERER_WEBGL: number } | null;
    getParameter(pname: number): unknown;
  } | null;
}

function readGpuRenderer(): string | undefined {
  const doc = (globalThis as { document?: { createElement?: (tag: string) => WebGlHost } }).document;
  if (!doc?.createElement) return undefined;
  try {
    const canvas = doc.createElement('canvas');
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    if (!gl) return undefined;
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    if (!dbg) return undefined;
    const renderer = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL);
    return typeof renderer === 'string' ? renderer : undefined;
  } catch {
    return undefined;
  }
}

function detectOs(ua: string, platform?: string): OsFamily {
  const hay = `${ua} ${platform ?? ''}`.toLowerCase();
  if (hay.includes('mac') || hay.includes('darwin') || hay.includes('apple m')) return 'macos';
  if (hay.includes('win')) return 'windows';
  return 'linux';
}

function matchAppleChip(ua: string): AppleChip | undefined {
  const lowered = ua.toLowerCase();
  const sorted = APPLE_CHIPS.slice().sort((a, b) => b.label.length - a.label.length);
  for (const chip of sorted) {
    const token = chip.label.toLowerCase().replace('apple ', '');
    if (lowered.includes(token)) return chip;
  }
  return undefined;
}

function hasAppleSiliconSignal(ctx: DetectContext, appleChip?: AppleChip): boolean {
  if (appleChip) return true;
  const hay = `${ctx.userAgent ?? ''} ${ctx.platform ?? ''} ${ctx.gpuRenderer ?? ''}`.toLowerCase();
  return hay.includes('apple m') || hay.includes('apple gpu');
}

function matchGpu(renderer: string | undefined): GpuProfile | undefined {
  if (!renderer) return undefined;
  const lower = renderer.toLowerCase();
  const candidates = GPUS.slice().sort((a, b) => b.label.length - a.label.length);
  for (const gpu of candidates) {
    const tokens = gpu.label.toLowerCase().replace('nvidia ', '').replace('amd radeon ', '').split(' ');
    if (tokens.every((t) => lower.includes(t))) return gpu;
  }
  if (lower.includes('apple')) return undefined;
  return undefined;
}

export function detectMachine(ctx: DetectContext = gatherContext()): DetectedMachine {
  const userAgent = ctx.userAgent ?? '';
  const os = detectOs(userAgent, ctx.platform);
  const notes: string[] = [];

  let accel: AccelKind = 'cpu';
  let appleChip: AppleChip | undefined;
  let gpu: GpuProfile | undefined;

  if (os === 'macos') {
    appleChip = matchAppleChip(userAgent) ?? matchAppleChip(ctx.gpuRenderer ?? '');
    if (hasAppleSiliconSignal(ctx, appleChip)) {
      accel = 'apple-silicon';
    } else {
      notes.push(
        'Browser could not confirm Apple Silicon. Treating this Mac as CPU-only until you pick the exact chip above.',
      );
    }
  } else {
    gpu = matchGpu(ctx.gpuRenderer);
    if (gpu) {
      accel = gpu.vendor === 'nvidia' ? 'cuda' : gpu.vendor === 'amd' ? 'rocm' : 'cpu';
    } else {
      notes.push(
        'WebGL did not expose a recognizable GPU — running the estimator as CPU-only. Pick your GPU above to unlock real speed estimates.',
      );
    }
  }

  const ramGb = ctx.deviceMemoryGb ? Math.max(4, ctx.deviceMemoryGb) : 16;
  if (!ctx.deviceMemoryGb) notes.push('Browser does not expose exact RAM — assuming 16 GB.');
  const vramGb =
    accel === 'apple-silicon'
      ? Math.max(4, Math.floor(ramGb * 0.85))
      : (gpu?.vramGb ?? Math.max(2, Math.floor(ramGb / 4)));

  const cpuLabel = appleChip?.label ?? gpu?.label ?? `${os} host · ${ctx.hardwareConcurrency ?? '?'} cores`;

  const hasChip = Boolean(appleChip || gpu);
  const hasRam = Boolean(ctx.deviceMemoryGb);
  const confidence: DetectedMachine['confidence'] =
    hasChip && hasRam ? 'high' : hasChip || hasRam ? 'medium' : 'low';

  return {
    confidence,
    os,
    accel,
    ramGb,
    vramGb,
    cpuLabel,
    appleChip,
    gpu,
    rawUserAgent: userAgent,
    notes,
  };
}

export function toMachineProfile(
  detected: DetectedMachine,
  installedRuntimes: RuntimeId[] = ['ollama'],
): MachineProfile {
  const id = detected.appleChip
    ? `scanned-${detected.appleChip.id}`
    : detected.gpu
      ? `scanned-${detected.gpu.id}`
      : detected.accel === 'apple-silicon'
        ? 'scanned-apple-silicon'
        : `scanned-${detected.os}`;
  return {
    id,
    nickname: detected.appleChip?.label ?? detected.gpu?.label ?? `${detected.os} host (detected)`,
    os: detected.os,
    cpuLabel: detected.cpuLabel,
    accel: detected.accel,
    appleChipId: detected.appleChip?.id,
    gpuId: detected.gpu?.id,
    ramGb: detected.ramGb,
    vramGb: detected.vramGb,
    storageFreeGb: 200,
    installedRuntimes,
    preferredRuntime: installedRuntimes[0],
  };
}

export function closestPreset(detected: DetectedMachine): MachineProfile | undefined {
  if (detected.appleChip) {
    return MACHINE_PRESETS.find((m) => m.appleChipId === detected.appleChip?.id);
  }
  if (detected.gpu) {
    return MACHINE_PRESETS.find((m) => m.gpuId === detected.gpu?.id);
  }
  return undefined;
}
